import React, { useState, useEffect } from 'react';
import { API_CONFIG } from '../../../config/api';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Database, Calendar, Globe, Download, RefreshCw, AlertTriangle, CheckCircle, Copy, ChevronDown, ChevronUp, Eye, EyeOff, Brain, Clock, XCircle, Zap, Star } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import toast from 'react-hot-toast';

interface CollectedData {
  id: string;
  task_id: string;
  execution_id: string;
  source_url: string;
  data_type?: string;
  raw_data: any;
  processed_data?: any;
  metadata?: any;
  hash?: string;
  is_valid: boolean;
  collected_at: string;
  created_at: string;
  ai_validation_status?: 'pending' | 'processing' | 'success' | 'failed' | 'error';
  ai_validation_score?: number;
  ai_validation_reason?: string;
  ai_validation_details?: any;
  ai_validated_at?: string;
  ai_validation_model?: string;
  scrape_task_executions?: {
    id: string;
    execution_number: number;
    status: string;
    started_at: string;
  };
  scrape_tasks?: {
    id: string;
    name: string;
    target_url: string;
  };
}

const ScrapeDataDetailViewer: React.FC = () => {
  const { taskId, dataId } = useParams<{ taskId: string; dataId: string }>();
  const navigate = useNavigate();
  
  const [data, setData] = useState<CollectedData | null>(null);
  const [loading, setLoading] = useState(true);
  const [showRawData, setShowRawData] = useState(false);
  const [showProcessedData, setShowProcessedData] = useState(false);
  const [showMetadata, setShowMetadata] = useState(false);
  const [showAIValidationDetails, setShowAIValidationDetails] = useState(false);

  useEffect(() => {
    if (dataId) {
      fetchDataDetail();
    }
  }, [dataId]);

  const fetchDataDetail = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/scrape-admin-manage?action=data_get&id=${dataId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.success && result.data) {
        setData(result.data);
      } else {
        toast.error('获取数据详情失败');
        navigate(`/admin/scrape/data/${taskId}`);
      }
    } catch (error) {
      console.error('获取数据详情失败:', error);
      toast.error('获取数据详情失败');
      navigate(`/admin/scrape/data/${taskId}`);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(`/admin/scrape/data/${taskId}`);
  };

  const handleCopyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success(`${label}已复制到剪贴板`);
    }).catch(() => {
      toast.error('复制失败');
    });
  };

  const handleExportData = () => {
    if (!data) return;
    
    const exportData = {
      id: data.id,
      task_id: data.task_id,
      source_url: data.source_url,
      data_type: data.data_type,
      is_valid: data.is_valid,
      collected_at: data.collected_at,
      raw_data: data.raw_data,
      processed_data: data.processed_data,
      metadata: data.metadata,
      hash: data.hash,
      // AI验证相关字段
      ai_validation_status: data.ai_validation_status,
      ai_validation_score: data.ai_validation_score,
      ai_validation_reason: data.ai_validation_reason,
      ai_validation_details: data.ai_validation_details,
      ai_validated_at: data.ai_validated_at,
      ai_validation_model: data.ai_validation_model
    };

    const dataStr = JSON.stringify(exportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `data_${data.id}_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast.success('数据导出成功');
  };

  const getAIValidationStatusDisplay = (status?: string, score?: number) => {
    if (!status) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          <Clock className="w-3 h-3 mr-1" />
          未验证
        </span>
      );
    }

    switch (status) {
      case 'pending':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <Clock className="w-3 h-3 mr-1" />
            待验证
          </span>
        );
      case 'processing':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            <Zap className="w-3 h-3 mr-1 animate-pulse" />
            验证中
          </span>
        );
      case 'success':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <Brain className="w-3 h-3 mr-1" />
            验证通过 {score !== undefined && `(${(score * 100).toFixed(0)}%)`}
          </span>
        );
      case 'failed':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <XCircle className="w-3 h-3 mr-1" />
            验证失败
          </span>
        );
      case 'error':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <AlertTriangle className="w-3 h-3 mr-1" />
            验证错误
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            <Clock className="w-3 h-3 mr-1" />
            未知状态
          </span>
        );
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center py-12">
          <Database className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">数据不存在或已被删除</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* 页面头部 */}
      <div className="mb-8">
        <div className="flex items-center mb-4">
          <button
            onClick={handleBack}
            className="flex items-center text-gray-600 hover:text-gray-900 mr-4"
          >
            <ArrowLeft className="w-5 h-5 mr-1" />
            返回数据列表
          </button>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                <Database className="w-6 h-6 mr-2 text-blue-600" />
                数据详情
              </h1>
              <div className="mt-2 text-sm text-gray-600 space-y-1">
                <div className="flex items-center">
                  <Globe className="w-4 h-4 mr-1" />
                  <span className="truncate">{data.source_url}</span>
                </div>
                {data.scrape_tasks && (
                  <div>任务: {data.scrape_tasks.name}</div>
                )}
                <div className="flex items-center">
                  <Calendar className="w-4 h-4 mr-1" />
                  采集时间: {new Date(data.collected_at).toLocaleString()}
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={fetchDataDetail}
                disabled={loading}
                className="flex items-center px-3 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
                title="刷新数据"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                刷新
              </button>
              <button
                onClick={handleExportData}
                className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
              >
                <Download className="w-4 h-4 mr-2" />
                导出数据
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 数据内容 */}
      <div className="space-y-6">
        {/* 基本信息卡片 */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">基本信息</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">数据ID</label>
              <div className="flex items-center space-x-2">
                <div className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md font-mono flex-1">
                  {data.id}
                </div>
                <button
                  onClick={() => handleCopyToClipboard(data.id, '数据ID')}
                  className="p-2 text-gray-400 hover:text-gray-600"
                  title="复制ID"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">数据类型</label>
              <div className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md">
                {data.data_type ? (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {data.data_type}
                  </span>
                ) : (
                  '未指定'
                )}
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">数据状态</label>
              <div className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  data.is_valid
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}>
                  {data.is_valid ? (
                    <><CheckCircle className="w-3 h-3 mr-1" />有效</>
                  ) : (
                    <><AlertTriangle className="w-3 h-3 mr-1" />无效</>
                  )}
                </span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">执行次数</label>
              <div className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md">
                {data.scrape_task_executions ? `#${data.scrape_task_executions.execution_number}` : '-'}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">采集时间</label>
              <div className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md">
                {new Date(data.collected_at).toLocaleString()}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">创建时间</label>
              <div className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md">
                {new Date(data.created_at).toLocaleString()}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">AI验证状态</label>
              <div className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md">
                {getAIValidationStatusDisplay(data.ai_validation_status, data.ai_validation_score)}
              </div>
            </div>
          </div>

          {data.hash && (
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">数据哈希</label>
              <div className="flex items-center space-x-2">
                <div className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md font-mono flex-1">
                  {data.hash}
                </div>
                <button
                  onClick={() => handleCopyToClipboard(data.hash!, '数据哈希')}
                  className="p-2 text-gray-400 hover:text-gray-600"
                  title="复制哈希"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* AI验证信息 */}
        {(data.ai_validation_status && data.ai_validation_status !== 'pending') && (
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                  <Brain className="w-5 h-5 mr-2 text-purple-500" />
                  AI验证信息
                </h2>
                {data.ai_validation_details && (
                  <button
                    onClick={() => setShowAIValidationDetails(!showAIValidationDetails)}
                    className="flex items-center px-3 py-2 text-sm bg-purple-50 text-purple-700 border border-purple-200 rounded-md hover:bg-purple-100 focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
                  >
                    {showAIValidationDetails ? (
                      <>
                        <EyeOff className="w-4 h-4 mr-1" />
                        隐藏详情
                        <ChevronUp className="w-4 h-4 ml-1" />
                      </>
                    ) : (
                      <>
                        <Eye className="w-4 h-4 mr-1" />
                        查看详情
                        <ChevronDown className="w-4 h-4 ml-1" />
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">验证状态</label>
                  <div className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md">
                    {getAIValidationStatusDisplay(data.ai_validation_status, data.ai_validation_score)}
                  </div>
                </div>

                {data.ai_validation_score !== undefined && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">验证分数</label>
                    <div className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md">
                      <div className="flex items-center">
                        <Star className="w-4 h-4 mr-1 text-yellow-500" />
                        {(data.ai_validation_score * 100).toFixed(1)}%
                      </div>
                    </div>
                  </div>
                )}

                {data.ai_validation_model && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">验证模型</label>
                    <div className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md">
                      {data.ai_validation_model}
                    </div>
                  </div>
                )}

                {data.ai_validated_at && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">验证时间</label>
                    <div className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md">
                      {new Date(data.ai_validated_at).toLocaleString()}
                    </div>
                  </div>
                )}
              </div>

              {data.ai_validation_reason && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">验证原因</label>
                  <div className="text-sm text-gray-900 bg-gray-50 p-4 rounded-md border">
                    {data.ai_validation_reason}
                  </div>
                </div>
              )}

              {data.ai_validation_details && showAIValidationDetails && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">验证详情</label>
                  <div className="bg-gray-50 p-4 rounded-md border max-h-96 overflow-auto">
                    <pre className="text-sm text-gray-700 whitespace-pre-wrap">
                      {JSON.stringify(data.ai_validation_details, null, 2)}
                    </pre>
                  </div>
                </div>
              )}

              {data.ai_validation_details && !showAIValidationDetails && (
                <div className="bg-gray-50 p-4 rounded-md">
                  <p className="text-sm text-gray-600 italic">
                    点击"查看详情"按钮展开AI验证详细信息
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* 来源URL */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">来源URL</h2>
          <div className="flex items-center space-x-2">
            <div className="text-sm text-gray-900 bg-gray-50 p-4 rounded-md break-all flex-1">
              {data.source_url}
            </div>
            <button
              onClick={() => handleCopyToClipboard(data.source_url, '来源URL')}
              className="p-2 text-gray-400 hover:text-gray-600"
              title="复制URL"
            >
              <Copy className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* 原始数据 */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                <Database className="w-5 h-5 mr-2 text-gray-500" />
                原始数据
                <span className="ml-2 text-sm text-gray-500 font-normal">
                  ({Object.keys(data.raw_data || {}).length} 个字段)
                </span>
              </h2>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleCopyToClipboard(JSON.stringify(data.raw_data, null, 2), '原始数据')}
                  className="flex items-center px-3 py-1 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  <Copy className="w-4 h-4 mr-1" />
                  复制
                </button>
                <button
                  onClick={() => setShowRawData(!showRawData)}
                  className="flex items-center px-3 py-2 text-sm bg-blue-50 text-blue-700 border border-blue-200 rounded-md hover:bg-blue-100 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  {showRawData ? (
                    <>
                      <EyeOff className="w-4 h-4 mr-1" />
                      隐藏数据
                      <ChevronUp className="w-4 h-4 ml-1" />
                    </>
                  ) : (
                    <>
                      <Eye className="w-4 h-4 mr-1" />
                      查看数据
                      <ChevronDown className="w-4 h-4 ml-1" />
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
          
          {showRawData && (
            <div className="p-6">
              <div className="bg-gray-50 p-4 rounded-md border max-h-96 overflow-auto">
                <pre className="text-sm text-gray-700 whitespace-pre-wrap">
                  {JSON.stringify(data.raw_data, null, 2)}
                </pre>
              </div>
            </div>
          )}
          
          {!showRawData && (
            <div className="p-6 bg-gray-50">
              <p className="text-sm text-gray-600 italic">
                点击"查看数据"按钮展开原始数据内容
              </p>
            </div>
          )}
        </div>

        {/* 处理后数据 */}
        {data.processed_data && (
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                  <CheckCircle className="w-5 h-5 mr-2 text-green-500" />
                  处理后数据
                  <span className="ml-2 text-sm text-gray-500 font-normal">
                    ({Object.keys(data.processed_data || {}).length} 个字段)
                  </span>
                </h2>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleCopyToClipboard(JSON.stringify(data.processed_data, null, 2), '处理后数据')}
                    className="flex items-center px-3 py-1 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    <Copy className="w-4 h-4 mr-1" />
                    复制
                  </button>
                  <button
                    onClick={() => setShowProcessedData(!showProcessedData)}
                    className="flex items-center px-3 py-2 text-sm bg-green-50 text-green-700 border border-green-200 rounded-md hover:bg-green-100 focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                  >
                    {showProcessedData ? (
                      <>
                        <EyeOff className="w-4 h-4 mr-1" />
                        隐藏数据
                        <ChevronUp className="w-4 h-4 ml-1" />
                      </>
                    ) : (
                      <>
                        <Eye className="w-4 h-4 mr-1" />
                        查看数据
                        <ChevronDown className="w-4 h-4 ml-1" />
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
            
            {showProcessedData && (
              <div className="p-6">
                <div className="bg-gray-50 p-4 rounded-md border max-h-96 overflow-auto">
                  <pre className="text-sm text-gray-700 whitespace-pre-wrap">
                    {JSON.stringify(data.processed_data, null, 2)}
                  </pre>
                </div>
              </div>
            )}
            
            {!showProcessedData && (
              <div className="p-6 bg-gray-50">
                <p className="text-sm text-gray-600 italic">
                  点击"查看数据"按钮展开处理后数据内容
                </p>
              </div>
            )}
          </div>
        )}

        {/* 元数据 */}
        {data.metadata && (
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                  <Globe className="w-5 h-5 mr-2 text-purple-500" />
                  元数据
                  <span className="ml-2 text-sm text-gray-500 font-normal">
                    ({Object.keys(data.metadata || {}).length} 个字段)
                  </span>
                </h2>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleCopyToClipboard(JSON.stringify(data.metadata, null, 2), '元数据')}
                    className="flex items-center px-3 py-1 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    <Copy className="w-4 h-4 mr-1" />
                    复制
                  </button>
                  <button
                    onClick={() => setShowMetadata(!showMetadata)}
                    className="flex items-center px-3 py-2 text-sm bg-purple-50 text-purple-700 border border-purple-200 rounded-md hover:bg-purple-100 focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
                  >
                    {showMetadata ? (
                      <>
                        <EyeOff className="w-4 h-4 mr-1" />
                        隐藏数据
                        <ChevronUp className="w-4 h-4 ml-1" />
                      </>
                    ) : (
                      <>
                        <Eye className="w-4 h-4 mr-1" />
                        查看数据
                        <ChevronDown className="w-4 h-4 ml-1" />
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
            
            {showMetadata && (
              <div className="p-6">
                <div className="bg-gray-50 p-4 rounded-md border max-h-96 overflow-auto">
                  <pre className="text-sm text-gray-700 whitespace-pre-wrap">
                    {JSON.stringify(data.metadata, null, 2)}
                  </pre>
                </div>
              </div>
            )}
            
            {!showMetadata && (
              <div className="p-6 bg-gray-50">
                <p className="text-sm text-gray-600 italic">
                  点击"查看数据"按钮展开元数据内容
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ScrapeDataDetailViewer;